import axios from "axios";
export type AuthEventType =
  | "FORCE_LOGOUT_PERSONAL"
  | "FORCE_LOGOUT_PUBLIC"
  | "USER_ACTIVITY"
  | "SESSION_TIMEOUT_WARNING" // 30-min timer fired — show modal on all tabs
  | "SESSION_TIMEOUT_KEEPALIVE"; // user clicked "Stay signed in" — reset all tabs

export interface AuthSyncEvent {
  type: AuthEventType;
  timestamp: number;
}

export interface UserSessionData {
  email?: string;
  firstname?: string;
  lastname?: string;
  refresh_token?: string;
  auth_provider?: string;
  has_accepted_terms?: boolean;
}

export const JML_HAS_ACCEPTED_TERMS_KEY = "JMLHasAcceptedTerms";

export const readHasAcceptedTermsFromStorage = (): boolean =>
  localStorage.getItem(JML_HAS_ACCEPTED_TERMS_KEY) === "true";

export const writeHasAcceptedTermsToStorage = (accepted: boolean): void => {
  localStorage.setItem(JML_HAS_ACCEPTED_TERMS_KEY, String(accepted));
};

export const clearHasAcceptedTermsFromStorage = (): void => {
  localStorage.removeItem(JML_HAS_ACCEPTED_TERMS_KEY);
};

let publicTokenPromise: Promise<string | null> | null = null;

export const fetchAndStorePublicToken = async (): Promise<string | null> => {
  try {
    // Public tokens are not available in binary mode, so we should return null immediately without trying to fetch or store a token
    if (process.env.REACT_APP_ENVIRONMENT === "binary") return null;

    const existingToken = localStorage.getItem("JMLPublicToken");
    const expiryTime = localStorage.getItem("JMLPublicTokenExpiry");
    const currentTime = Date.now();
    const expiryMs = expiryTime !== null ? Number(expiryTime) : NaN;
    const isExpired = !Number.isFinite(expiryMs) || currentTime >= expiryMs;

    if (existingToken && !isExpired) {
      return existingToken;
    }

    if (publicTokenPromise) {
      return publicTokenPromise;
    }

    publicTokenPromise = (async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_SIMPLIFAI_BACKEND_API_URL}/api/get_public_token`,
        );
        if (res.data?.success && res.data?.token) {
          const token = res.data.token;
          localStorage.setItem("JMLPublicToken", token);

          // Extract expiry from token if possible, otherwise fallback to 1 hour
          let newExpiry = Date.now() + 3600000;
          try {
            const tokenParts = token.split(".");
            if (tokenParts.length > 1) {
              const payload = JSON.parse(atob(tokenParts[1]));
              if (payload.exp) {
                // JWT exp is in seconds, convert to milliseconds
                newExpiry = payload.exp * 1000;
              }
            }
          } catch (e) {
            console.error("Failed to parse token expiry, using default 1h:", e);
          }

          localStorage.setItem("JMLPublicTokenExpiry", newExpiry.toString());
          return token;
        }
        return null;
      } finally {
        publicTokenPromise = null;
      }
    })();

    return await publicTokenPromise;
  } catch (error) {
    console.error("Failed to fetch public token:", error);
    publicTokenPromise = null;
    return null;
  }
};

export const getPublicProjects = (): Record<string, string> => {
  try {
    const publicProjectsString = process.env.REACT_APP_PUBLIC_PROJECTS || "{}";
    return JSON.parse(publicProjectsString);
  } catch (error) {
    console.error("Failed to parse REACT_APP_PUBLIC_PROJECTS:", error);
    return {};
  }
};

export const isPublicSlug = (slug: string): boolean => {
  if (!slug) return false;
  // Slug url is not available in binary mode, so we should return false immediately without trying to fetch or check a slug
  if (process.env.REACT_APP_ENVIRONMENT === "binary") return false;
  const publicProjects = getPublicProjects();
  return Object.keys(publicProjects).includes(slug);
};

export const isPublicProjectId = (projectId: string): boolean => {
  if (!projectId) return false;
  if (process.env.REACT_APP_ENVIRONMENT === "binary") return false;
  const publicProjects = getPublicProjects();
  return Object.values(publicProjects).includes(projectId);
};

export const getProjectIdFromSlug = (slug: string): string | null => {
  if (!slug) return null;
  // Slug url is not available in binary mode, so we should return false immediately without trying to fetch or check a slug
  if (process.env.REACT_APP_ENVIRONMENT === "binary") return null;
  const publicProjects = getPublicProjects();
  return publicProjects[slug] || null;
};

/** Resolve a canvas URL segment to a project id (public slug → env id, else raw segment). */
export const resolveProjectIdFromPathSegment = (
  segment: string | null | undefined,
): string | null => {
  const trimmed = segment?.trim();
  if (!trimmed) return null;
  return getProjectIdFromSlug(trimmed) || trimmed;
};

/** True when pathname is an active canvas project route (`/canvas/:idOrSlug`). */
export const isCanvasProjectRoute = (pathname: string): boolean =>
  /^\/canvas\/[^/]+/.test(pathname);

/** Progress scan toasts are canvas-only; other toast types show on any route. */
export const shouldShowGlobalToastOnRoute = (
  pathname: string,
  toast: { show: boolean; type: string },
): boolean =>
  toast.show && (toast.type !== "progress" || isCanvasProjectRoute(pathname));

/** Read the active canvas project id from the current URL (for non-React callers). */
export const getCanvasProjectIdFromLocation = (): string | null => {
  const match = window.location.pathname.match(/^\/canvas\/([^/]+)/);
  if (!match?.[1]) return null;
  return resolveProjectIdFromPathSegment(match[1]);
};

const IDE_PROJECT_NAME_PREFIX = /^ide_/i;

/** Strip IDE-internal `ide_` prefix for public canvas display only. */
export const displayPublicProjectName = (name: string | undefined | null): string => {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return trimmed;
  return trimmed.replace(IDE_PROJECT_NAME_PREFIX, "");
};

const channel = new BroadcastChannel("auth_channel");

export const authSync = {
  broadcast: (event: { type: AuthEventType }) => {
    const payload: AuthSyncEvent = { ...event, timestamp: Date.now() };
    channel.postMessage(payload);

    localStorage.setItem("AUTH_SYNC", JSON.stringify(payload));

    localStorage.removeItem("AUTH_SYNC");
  },

  listen: (callback: (event: AuthSyncEvent) => void) => {
    const handleMessage = (e: MessageEvent) => {
      if (e.data?.type) callback(e.data);
    };

    const handleStorage = (e: StorageEvent) => {
      if (e.key === "AUTH_SYNC" && e.newValue) {
        try {
          const event = JSON.parse(e.newValue);
          if (event.type) callback(event);
        } catch (err) {
          console.error("Failed to parse AUTH_SYNC storage event", err);
        }
      }
    };

    channel.addEventListener("message", handleMessage);
    window.addEventListener("storage", handleStorage);

    return () => {
      channel.removeEventListener("message", handleMessage);
      window.removeEventListener("storage", handleStorage);
    };
  },
};
